/**
 * Handle editing in the 3D view (PointInteractionVolumetricViewManager /
 * PointInteractionVolumetricViewHandler): hover and drag the adjustable handles (landmark
 * points, contour edge and centre handles), snapping to the segmentation surfaces and/or the
 * first visible voxel, Ctrl to drag freely, Ctrl+wheel to push or pull along the view ray,
 * double-click to enter edit mode (or to leave every edit mode on empty space). The coaptation
 * point stays on the Root Centroid; a contour centre handle rides the rail; contour edge
 * handles snap to the surface only.
 *
 * Listens in the capture phase on the same element as the camera controls, so a drag that
 * starts on a handle never reaches the orbit.
 */
import * as THREE from 'three';
import type { TheaterSession } from '@/theater/TheaterSession';
import { closestPointOnPolyline } from './geometry';

const SCREEN_PICK_PX = 18;
const LIVE_INTERVAL_MS = 30;
const DEPTH_PER_NOTCH = 0.02;

interface Drag {
  key: string;
  planeNormal: THREE.Vector3; // world
  planePoint: THREE.Vector3; // world, the handle at grab time
  grabOffset: THREE.Vector3; // world: handle - pointer hit on the plane
  lastLive: number;
  pending: THREE.Vector3 | null; // scene mm
  moved: boolean;
}

export class EditController {
  private session: TheaterSession;
  private element: HTMLElement;
  private raycaster = new THREE.Raycaster();
  private hover: string | null = null;
  private drag: Drag | null = null;
  private liveTimer: number | null = null;

  constructor(session: TheaterSession, element: HTMLElement) {
    this.session = session;
    this.element = element;
    this.raycaster.layers.enableAll();
    element.addEventListener('pointerdown', this.onPointerDown, true);
    element.addEventListener('pointermove', this.onPointerMove, true);
    element.addEventListener('pointerup', this.onPointerUp, true);
    element.addEventListener('pointercancel', this.onPointerUp, true);
    element.addEventListener('dblclick', this.onDoubleClick, true);
    element.addEventListener('wheel', this.onWheel, { capture: true, passive: false });
  }

  dispose(): void {
    const el = this.element;
    el.removeEventListener('pointerdown', this.onPointerDown, true);
    el.removeEventListener('pointermove', this.onPointerMove, true);
    el.removeEventListener('pointerup', this.onPointerUp, true);
    el.removeEventListener('pointercancel', this.onPointerUp, true);
    el.removeEventListener('dblclick', this.onDoubleClick, true);
    el.removeEventListener('wheel', this.onWheel, true);
    if (this.liveTimer) window.clearTimeout(this.liveTimer);
  }

  get dragging(): string | null {
    return this.drag?.key ?? null;
  }

  // ------------------------------------------------------------ picking
  private ndc(e: { clientX: number; clientY: number }): THREE.Vector2 {
    const r = this.element.getBoundingClientRect();
    return new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -(((e.clientY - r.top) / r.height) * 2 - 1));
  }

  private setRay(e: { clientX: number; clientY: number }): void {
    this.raycaster.setFromCamera(this.ndc(e), this.session.viewer.controls.camera);
  }

  /** The handle under the pointer: a raycast hit first, else the nearest within 18 px. */
  private pick(e: PointerEvent | MouseEvent, onlyEditing: boolean): string | null {
    const handles = this.session.editableHandles(onlyEditing);
    if (handles.length === 0) return null;
    this.setRay(e);
    const hits = this.raycaster.intersectObjects(
      handles.map((h) => h.mesh),
      false,
    );
    if (hits.length) return hits[0].object.name.replace('handle:', '');
    const r = this.element.getBoundingClientRect();
    const cam = this.session.viewer.controls.camera;
    let best: string | null = null;
    let bestD = SCREEN_PICK_PX;
    for (const h of handles) {
      const p = h.mesh.getWorldPosition(new THREE.Vector3()).project(cam);
      if (p.z > 1) continue;
      const sx = ((p.x + 1) / 2) * r.width + r.left;
      const sy = ((1 - p.y) / 2) * r.height + r.top;
      const d = Math.hypot(sx - e.clientX, sy - e.clientY);
      if (d < bestD) {
        bestD = d;
        best = h.key;
      }
    }
    return best;
  }

  private setHover(key: string | null): void {
    if (this.hover === key) return;
    const prev = this.hover;
    this.hover = key;
    if (prev) this.refreshHandle(prev);
    if (key) this.refreshHandle(key);
    this.element.style.cursor = key ? 'grab' : '';
    this.session.viewer.invalidate('main');
  }

  refreshHandle(key: string): void {
    this.session.setHandleVisual(key, { hover: this.hover === key, dragging: this.drag?.key === key });
  }

  refreshAll(): void {
    for (const h of this.session.editableHandles(false)) this.refreshHandle(h.key);
  }

  // ------------------------------------------------------------ events
  private onPointerDown = (e: PointerEvent): void => {
    if (e.pointerType === 'touch' || e.button !== 0 || (e.buttons & 2) !== 0) return;
    const key = this.pick(e, true);
    if (!key) return;
    const mesh = this.session.editableHandles(true).find((h) => h.key === key)?.mesh;
    if (!mesh) return;
    const cam = this.session.viewer.controls.camera;
    const world = mesh.getWorldPosition(new THREE.Vector3());
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
    this.setRay(e);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(forward, world);
    const hit = this.raycaster.ray.intersectPlane(plane, new THREE.Vector3()) ?? world.clone();
    this.drag = { key, planeNormal: forward, planePoint: world, grabOffset: world.clone().sub(hit), lastLive: 0, pending: null, moved: false };
    this.session.viewer.controls.enabled = false;
    this.element.setPointerCapture(e.pointerId);
    this.element.style.cursor = 'grabbing';
    this.refreshHandle(key);
    e.stopImmediatePropagation();
    e.preventDefault();
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.drag) {
      if (e.buttons === 0) this.setHover(this.pick(e, true));
      return;
    }
    e.stopImmediatePropagation();
    const d = this.drag;
    const cam = this.session.viewer.controls.camera;
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
    d.planeNormal.copy(forward);
    this.setRay(e);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(d.planeNormal, d.planePoint);
    const hit = this.raycaster.ray.intersectPlane(plane, new THREE.Vector3());
    if (!hit) return;
    let world = hit.add(d.grabOffset);
    const constraint = this.session.corrections?.constraintFor(d.key) ?? 'free';
    if (!e.ctrlKey && (constraint === 'free' || constraint === 'surface')) {
      const snapped = this.session.snapAlongRay(this.raycaster.ray, constraint === 'surface' ? 'surface-only' : 'mode');
      if (snapped) world = snapped;
    }
    let scene = this.session.worldToScene(world);
    if (constraint === 'centerline') {
      const line = this.session.centerlinePoints();
      if (line.length >= 2) scene = closestPointOnPolyline(line, scene).point;
    }
    d.planePoint.copy(this.session.sceneToWorld(scene));
    d.moved = true;
    this.queueLive(scene);
  };

  private queueLive(scene: THREE.Vector3): void {
    const d = this.drag;
    if (!d) return;
    d.pending = scene;
    const now = performance.now();
    const due = Math.max(0, LIVE_INTERVAL_MS - (now - d.lastLive));
    if (this.liveTimer) return;
    this.liveTimer = window.setTimeout(() => {
      this.liveTimer = null;
      const dd = this.drag;
      if (!dd?.pending) return;
      dd.lastLive = performance.now();
      this.session.moveHandle(dd.key, dd.pending, true);
      dd.pending = null;
    }, due);
  }

  private onPointerUp = (e: PointerEvent): void => {
    const d = this.drag;
    if (!d) return;
    e.stopImmediatePropagation();
    this.drag = null;
    if (this.liveTimer) {
      window.clearTimeout(this.liveTimer);
      this.liveTimer = null;
    }
    this.session.viewer.controls.enabled = true;
    if (this.element.hasPointerCapture(e.pointerId)) this.element.releasePointerCapture(e.pointerId);
    this.element.style.cursor = this.hover ? 'grab' : '';
    const final = d.pending ?? (d.moved ? this.session.worldToScene(d.planePoint) : null);
    if (final) this.session.moveHandle(d.key, final, false);
    else this.refreshHandle(d.key);
  };

  /** Ctrl+wheel while dragging: push or pull the point along the view ray. */
  private onWheel = (e: WheelEvent): void => {
    const d = this.drag;
    if (!d || !e.ctrlKey) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const cam = this.session.viewer.controls.camera;
    const notches = Math.max(-5, Math.min(5, -Math.sign(e.deltaY)));
    const toPoint = d.planePoint.clone().sub(cam.position);
    const factor = 1 + DEPTH_PER_NOTCH * notches;
    const next = cam.position.clone().add(toPoint.multiplyScalar(factor));
    const minDepth = Math.max(0.02, this.session.volume?.cameraClipDistance ?? 0);
    if (next.distanceTo(cam.position) < minDepth) return;
    d.planePoint.copy(next);
    d.moved = true;
    this.queueLive(this.session.worldToScene(next));
  };

  private onDoubleClick = (e: MouseEvent): void => {
    const corr = this.session.corrections;
    if (!corr) return;
    const key = this.pick(e, false);
    if (key && !key.startsWith('contour:')) corr.setEditing(key, true);
    else if (!key) corr.clearEditing();
    this.refreshAll();
    this.session.viewer.invalidate('main');
    e.stopImmediatePropagation();
  };
}
